/* Copyright (c) 2020-present CloudIO Inc */

import { attachmentProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './AttachmentColumn';

const AttachmentColumnManager: ComponentManagerFn<'AttachmentColumn'> = (
  platform: Platform,
): ComponentManager<'AttachmentColumn'> => {
  return {
    ...baseComponentManager<'AttachmentColumn'>(platform, 'AttachmentColumn'),
    type: 'AttachmentColumn',
    name: 'Attachment Column',
    isChildAllowed: (page, item, childType) =>
      (['ColumnFooter', 'TriggerOnChange'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => ['Attachment'],
      },
      ...attachmentProps,
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'accept': {
          return !itemData.images;
        }
        case 'imageHeight':
        case 'showImage':
        case 'maxImageWidth': {
          return itemData.images;
        }
        case 'toolTip':
        case 'toolTipPosition':
        case 'toolTipMaxWidth':
          return false;
        default:
          return true;
      }
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    category: 'Data Grid',
    icon: { iconName: 'paperclip' },
    description: `**Overview**<br/>
The Attachment Column is a specialized Data Grid column designed to handle file storage. It embeds a file upload control directly into each grid row, allowing users to upload, view, or download documents and images associated with a specific record.
Depending on configuration, it can render as a simple "Paperclip" link for documents (PDFs, Docs) or as a thumbnail preview for images.

**1. Data Binding**<br/>
Unlike standard text columns, this component requires a specific data type to function correctly.
* **View Attribute:** Select the attribute in your Data Store where the file reference will be saved.
* **Requirement:** The attribute in the backend Data Source must be of type **Attachment**. You cannot bind this to a standard String field.

**2. Display Modes (File vs. Image)**<br/>
You can configure the column to handle general files or specifically optimize it for photos.
* **Images (Toggle):**
    * **Enabled (True):** The column treats uploads as images. (Options: **Show Image** renders a thumbnail preview; **Image Height / Max Width** controls dimensions to keep the grid row height manageable).
    * **Disabled (False):** The column treats uploads as generic files. (Options: **Accept** defines allowed file types like .pdf,.docx; Visuals render a clickable link or icon).

**3. Interaction & Logic**<br/>
File uploads often trigger downstream processes (e.g., validation, renaming, or saving metadata).
* **TriggerOnChange:** You can add this child component to execute logic immediately after a file is successfully uploaded.
* **Accessing File Metadata:** Inside your trigger logic, you can access detailed information about the uploaded file using the following expressions:
    * \`$._.file.fileName\` (Name of the file)
    * \`$._.file.fileSize\` (Size in bytes)
    * \`$._.file.contentType\` (MIME type, e.g., image/png)
    * \`$._.file.fileUid\` (Unique ID of the file)`,
  };
};

export default AttachmentColumnManager;
